import { LightMode } from "@/components/ui/color-mode";
import { InputField } from "@/components/input-field/InputField";
import { ComboboxComponent } from "@/components/select/Combobox";
import { AppColors } from "@/styles/colors";
import {
  Button,
  CloseButton,
  Dialog,
  Flex,
  MenuOpenChangeDetails,
  Portal,
  Text,
} from "@chakra-ui/react";
import { useEffect, useRef } from "react";
import { useForm, useWatch } from "react-hook-form";

import type { LegalOnboardingDomicilio } from "../schema/handler-functions";
import { useDomicilioData } from "../flows/useDomicilioData";

interface Props {
  open: boolean;
  onOpenChange: (e: MenuOpenChangeDetails) => void;
  loadingConfirm: boolean;
  onCancel: () => void;
  onConfirm: (domicilio: LegalOnboardingDomicilio) => void;
  initialDomicilio?: LegalOnboardingDomicilio | null;
}

export const DomicilioModal = ({
  open,
  onOpenChange,
  loadingConfirm,
  onCancel,
  onConfirm,
  initialDomicilio,
}: Props) => {
  const {
    provincias,
    localidades,
    loadingProvincias,
    loadingLocalidades,
    fetchProvincias,
    fetchLocalidades,
    clearLocalidades,
    getCodigoPostal,
  } = useDomicilioData();

  const { control, reset, getValues, setValue } = useForm({
    defaultValues: {
      calle: "",
      numero: "",
      piso: "",
      codigoPostal: "",
      provinciaId: "",
      localidadId: "",
    },
  });

  const provinciaValue = useWatch({ name: "provinciaId", control });
  const localidadValue = useWatch({ name: "localidadId", control });
  const calle = useWatch({ name: "calle", control });
  const numero = useWatch({ name: "numero", control });
  const codigoPostal = useWatch({ name: "codigoPostal", control });

  const provinciaId = Number(provinciaValue) || 0;
  const localidadId = Number(localidadValue) || 0;

  const isInitializingRef = useRef(false);
  const prevProvinciaIdRef = useRef<number>(0);

  useEffect(() => {
    if (isInitializingRef.current) {
      isInitializingRef.current = false;
      prevProvinciaIdRef.current = provinciaId;
      return;
    }

    if (prevProvinciaIdRef.current !== provinciaId) {
      setValue("localidadId", "", { shouldValidate: true });
      setValue("codigoPostal", "", { shouldValidate: true });
      clearLocalidades();
      prevProvinciaIdRef.current = provinciaId;
    }
  }, [provinciaId, setValue, clearLocalidades]);

  useEffect(() => {
    const cp = getCodigoPostal(localidadId);
    setValue("codigoPostal", cp, { shouldValidate: true });
  }, [localidadId, getCodigoPostal, setValue]);

  useEffect(() => {
    if (open) {
      isInitializingRef.current = true;
      reset({
        calle: initialDomicilio?.calle ?? "",
        numero: initialDomicilio?.numero ?? "",
        piso: initialDomicilio?.piso ?? "",
        codigoPostal: initialDomicilio?.codigoPostal ?? "",
        provinciaId: initialDomicilio?.provinciaId
          ? String(initialDomicilio.provinciaId)
          : "",
        localidadId: initialDomicilio?.localidadId
          ? String(initialDomicilio.localidadId)
          : "",
      });
      fetchProvincias();
    }
  }, [initialDomicilio, open, reset, fetchProvincias]);

  useEffect(() => {
    if (open && provinciaId) {
      fetchLocalidades(provinciaId);
    }
  }, [open, provinciaId, fetchLocalidades]);

  const isLoadingAny = loadingConfirm || loadingProvincias || loadingLocalidades;
  const hasRequiredFields = calle?.trim() && numero?.trim() && codigoPostal?.trim();
  const hasValidIds = provinciaId && localidadId;

  const confirmDisabled = isLoadingAny || !hasRequiredFields || !hasValidIds;

  const handleConfirm = () => {
    if (confirmDisabled) return;

    const values: any = getValues();

    onConfirm({
      calle: String(values.calle ?? ""),
      numero: String(values.numero ?? ""),
      piso: String(values.piso ?? ""),
      codigoPostal: String(values.codigoPostal ?? ""),
      provinciaId,
      localidadId,
    });
  };

  return (
    <Dialog.Root
      placement="center"
      lazyMount
      open={open}
      onOpenChange={onOpenChange}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <LightMode>
            <Dialog.Content width={{ base: "calc(100vw - 32px)", md: "720px" }} maxW="720px">
              <Dialog.Header paddingY="16px" paddingLeft="24px">
                <Dialog.Title
                  fontWeight={600}
                  color={AppColors.GRAY_TEXT}
                  fontSize="18px"
                >
                  Datos de domicilio
                </Dialog.Title>
              </Dialog.Header>

              <Dialog.Body paddingX="24px" paddingY="8px" width="100%">
                <Flex flexDir="column" gap="12px">
                  <Text fontSize="16px" color={AppColors.GRAY_TEXT}>
                    Completá el domicilio de la persona jurídica.
                  </Text>

                  <Flex flexDir={{ base: "column", md: "row" }} gap="12px">
                    <Flex flex="2" flexDir="column" gap="6px">
                      <InputField
                        control={control as any}
                        inputName="calle"
                        labelText="Calle"
                        required
                      />
                    </Flex>
                    <Flex flex="1" flexDir="column" gap="6px">
                      <InputField
                        control={control as any}
                        inputName="numero"
                        labelText="Número"
                        required
                      />
                    </Flex>
                  </Flex>

                  <Flex flexDir={{ base: "column", md: "row" }} gap="12px">
                    <Flex flex="1" flexDir="column" gap="6px">
                      <InputField
                        control={control as any}
                        inputName="piso"
                        labelText="Piso"
                      />
                    </Flex>
                    <Flex flex="1" flexDir="column" gap="6px">
                      <InputField
                        control={control as any}
                        inputName="codigoPostal"
                        labelText="Código postal"
                        required
                        disabled
                      />
                    </Flex>
                  </Flex>

                  <Flex flexDir={{ base: "column", md: "row" }} gap="12px">
                    <Flex flex="1" flexDir="column" gap="6px">
                      <ComboboxComponent
                        key={`provincia-${provincias?.length ?? 0}`}
                        control={control as any}
                        name="provinciaId"
                        placeholder="Buscar provincia"
                        loading={loadingProvincias}
                        disabled={loadingProvincias}
                        items={(provincias ?? []).map((p) => ({
                          label: p.name,
                          value: String(p.id),
                        }))}
                        fullWidth
                        menuMaxHeight="250px"
                        label="Provincia"
                        required
                        disablePortal
                      />
                    </Flex>

                    <Flex flex="1" flexDir="column" gap="6px">
                      <ComboboxComponent
                        key={`localidad-${provinciaId}-${localidades?.length ?? 0}`}
                        control={control as any}
                        name="localidadId"
                        placeholder="Buscar localidad"
                        loading={loadingProvincias || loadingLocalidades}
                        disabled={
                          loadingProvincias ||
                          loadingLocalidades ||
                          !provinciaId
                        }
                        items={(localidades ?? []).map((l) => ({
                          label: l.externalCode ? `${l.name} (${l.externalCode})` : l.name,
                          value: String(l.id),
                        }))}
                        fullWidth
                        menuMaxHeight="250px"
                        label="Localidad"
                        required
                        disablePortal
                      />
                    </Flex>
                  </Flex>
                </Flex>
              </Dialog.Body>

              <Dialog.Footer paddingY="16px" paddingX="24px">
                <Dialog.ActionTrigger asChild>
                  <Button variant="outline" paddingX="16px" fontWeight={600} onClick={onCancel}>
                    Cancelar
                  </Button>
                </Dialog.ActionTrigger>

                <Button
                  paddingX="16px"
                  bgColor={AppColors.GREEN_PRIMARY}
                  fontWeight={600}
                  onClick={handleConfirm}
                  loading={loadingConfirm}
                  disabled={confirmDisabled}
                >
                  Continuar
                </Button>
              </Dialog.Footer>

              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Content>
          </LightMode>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
